// src/components/ui/MarkdownLite.tsx
// Renderer markdown minimale e senza dipendenze, pensato per l'output della chat AI:
// grassetto (**testo**), elenchi puntati (- / *), elenchi numerati (1.), titoli (#/##/###),
// e ritorni a capo. Sufficiente per il markdown "leggero" prodotto da Gemini.
import React from 'react';

/** Rende inline il grassetto **testo** (il resto è testo semplice). */
const renderInline = (text: string): React.ReactNode[] => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**')) {
            return <strong key={i}>{p.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={i}>{p}</React.Fragment>;
    });
};

interface Props {
    text: string;
    className?: string;
}

const MarkdownLite: React.FC<Props> = ({ text, className = '' }) => {
    const lines = (text || '').split('\n');
    const blocks: React.ReactNode[] = [];
    let list: { ordered: boolean; items: string[] } | null = null;

    const flushList = (key: number) => {
        if (!list) return;
        const items = list.items.map((it, i) => <li key={i}>{renderInline(it)}</li>);
        blocks.push(
            list.ordered ? (
                <ol key={`l${key}`} className="list-decimal pl-5 space-y-1 my-1">{items}</ol>
            ) : (
                <ul key={`l${key}`} className="list-disc pl-5 space-y-1 my-1">{items}</ul>
            )
        );
        list = null;
    };

    lines.forEach((raw, idx) => {
        const line = raw.trimEnd();
        const bullet = line.match(/^\s*[-*]\s+(.*)$/);
        const numbered = line.match(/^\s*\d+\.\s+(.*)$/);
        const heading = line.match(/^(#{1,3})\s+(.*)$/);

        if (bullet) {
            if (!list || list.ordered) { flushList(idx); list = { ordered: false, items: [] }; }
            list.items.push(bullet[1]);
            return;
        }
        if (numbered) {
            if (!list || !list.ordered) { flushList(idx); list = { ordered: true, items: [] }; }
            list.items.push(numbered[1]);
            return;
        }
        flushList(idx);
        if (heading) {
            blocks.push(<p key={idx} className="font-bold text-gray-900 mt-1">{renderInline(heading[2])}</p>);
        } else if (line.trim() === '') {
            blocks.push(<div key={idx} className="h-2" />);
        } else {
            blocks.push(<p key={idx}>{renderInline(line)}</p>);
        }
    });
    flushList(lines.length);

    return <div className={className}>{blocks}</div>;
};

export default MarkdownLite;
